// src/pages/MovementsDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Button, Table, message, Space, Typography, Modal, Form, DatePicker, TimePicker } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { diaryAPI } from '../utils/api';
import dayjs from 'dayjs';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const MovementsDiary: React.FC = () => {
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getMovementEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading movement entries:', error);
      message.error('Ошибка загрузки записей шевелений');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    const movementTime = record.movement_time ? dayjs(record.movement_time) : dayjs();
    form.setFieldsValue({
      entry_date: record.entry_date ? dayjs(record.entry_date) : movementTime,
      movement_time: movementTime,
    });
    setModalVisible(true);
  };

  const onFinish = async (values: any) => {
    try {
      const entryDate = values.entry_date
        ? values.entry_date.format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD');
      const movementTime = values.movement_time
        ? values.movement_time.format('HH:mm:ss')
        : dayjs().format('HH:mm:ss');
      const data = {
        entry_date: entryDate,
        movement_time: movementTime,
      };

      if (editingId) {
        await diaryAPI.updateMovementEntry(editingId, data);
        message.success('Шевеление успешно обновлено!');
      } else {
        await diaryAPI.createMovementEntry(data);
        message.success('Шевеление зафиксировано!');
      }
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving movement entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения шевеления');
    }
  };

  const handleAddMovement = async () => {
    try {
      await diaryAPI.createMovementEntry({});
      message.success('Шевеление зафиксировано!');
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving movement entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения шевеления');
    }
  };

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  const groupedEntries: Record<string, any[]> = {};
  entries.forEach((entry: any) => {
    const date = dayjs(entry.entry_date || entry.movement_time).format('YYYY-MM-DD');
    if (!groupedEntries[date]) groupedEntries[date] = [];
    groupedEntries[date].push(entry);
  });

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ color: '#263238' }}>Дневник шевелений</Title>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: 24 }}>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddMovement}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}
            size="large"
          >
            Зафиксировать шевеление
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        {Object.entries(groupedEntries)
          .sort(([dateA], [dateB]) => dateB.localeCompare(dateA))
          .map(([date, dateEntries]) => (
          <div key={date} style={{ marginBottom: 24 }}>
            <Title level={4}>{dayjs(date).format('DD.MM.YYYY')} - {dateEntries.length} шевелений</Title>
            <Table
              columns={columns}
              dataSource={dateEntries}
              loading={loading}
              rowKey="id"
              pagination={false}
              size="small"
            />
          </div>
        ))}
        {entries.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <p>Нет записей о шевелениях</p>
          </div>
        )}
      </Card>

      <Modal
        title="Редактировать шевеление"
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={500}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            label="Дата"
            name="entry_date"
            rules={[{ required: true, message: 'Укажите дату!' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" size="large" />
          </Form.Item>

          <Form.Item
            label="Время"
            name="movement_time"
            rules={[{ required: true, message: 'Укажите время!' }]}
          >
            <TimePicker style={{ width: '100%' }} format="HH:mm:ss" size="large" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                Обновить
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
